import {createSelector} from 'reselect'
import {RootState} from 'store'

import {Overlay, OverlayType} from './types'

// TODO: remove optional chaining, state should conform to type
/**
 * Return only the active overlay and beneath.
 * We keep data for those on top around for browser-forward actions
 */
export const getOverlay = createSelector(
  [
    ({overlay}: RootState) => overlay?.activeOverlay,
    ({overlay}: RootState) => overlay?.overlays
  ],
  (activeOverlay, overlays = []) => {
    const activeIndex = overlays.findIndex(o => o.id === activeOverlay)
    return overlays.slice(0, activeIndex + 1)
  }
)

/**
 * Factory function that accepts the `OverlayType` and returns the `visibilityState`
 */
export const getOverlayVisibilityState = createSelector(
  [getOverlay, (_, type: OverlayType) => type],
  (overlay, type): boolean => overlay.some(overlay => overlay.type === type)
)

//TODO: Replace other selectors with getOverlayVisibilityState and rename it to getOverlayVisibility
// https://app.shortcut.com/findhotel/story/94192/use-getoverlayvisibilitystate-instead-of-all-the-selectors-inside-the-overlay-directory

export const getOverlayVisibility = createSelector(
  [getOverlay, (_, type: OverlayType) => type],
  (overlay, type): boolean => overlay.some(overlay => overlay.type === type)
)

export const getLocationVisibility = createSelector(getOverlay, overlay =>
  overlay.some(overlay => overlay.type === OverlayType.LocationOverlay)
)

export const getHotelFacilitiesVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.HotelFacilities)

export const getShareYourFeedbackVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.ShareYourFeedbackOverlay)

export const getPreferencesVisibility = createSelector(getOverlay, overlay =>
  overlay.some(overlay => overlay.type === OverlayType.Preferences)
)

export const getFiltersVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.Filters)

export const getPriceFilterVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.PriceFilter)

export const getPropertyTypesFilterVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.PropertyTypeFilter)

export const getReviewScoreFilterVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.ReviewScoreFilter)

export const getFacilitiesFilterVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.FacilitiesFilter)

export const getSortByVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.SortBy)

export const getIsDatePickerOpen = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.DatePicker)

export const getIsDestinationPickerOpen = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.DestinationPicker)

export const getIsGuestPickerOpen = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.GuestPicker)

export const getIsHotelDatePickerOpen = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.HotelDatePicker)

export const getIsHotelGuestPickerOpen = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.HotelGuestPicker)

export const getAvailabilityPopupVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.AvailabilityPopup)

export const getVerticalGalleryOverlayVisibility = (
  state: RootState
): boolean => getOverlayVisibility(state, OverlayType.VerticalGalleryOverlay)

export const getRoomDetailOverlayVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.RoomDetailsOverlay)

export const getGuestReviewsOverlayVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.GuestReviews)

export const getStarRatingFilterVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.StarRatingsFilter)

export const getHowSearchWorkOverlay = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.HowSearchWorks)

export const getAiPoweredReviewsPanelVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.AiPoweredReviewsPanel)

export const getDealDetailsOverlayVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.DealDetails)

export const getBookOnAppConfirmationOverlayVisibility = (
  state: RootState
): boolean => getOverlayVisibility(state, OverlayType.BookOnAppConfirmation)

export const getCashbackInfoOverlayVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.CashbackInfo)

/**
 * Find an overlay by type in a type-safe way
 */
const findOverlayByType = <Type extends OverlayType>(
  overlays: Overlay[],
  type: Type
) =>
  overlays.find(
    (overlay): overlay is Extract<Overlay, {type: Type}> =>
      overlay.type === type
  )

export const getLightboxOverlay = createSelector(getOverlay, overlay =>
  findOverlayByType(overlay, OverlayType.LightboxGalleryOverlay)
)

export const getHotelPoliciesOverlay = createSelector(getOverlay, overlay =>
  findOverlayByType(overlay, OverlayType.HotelPolicies)
)

export const getHotelMoreLocationInfoOverlay = createSelector(
  getOverlay,
  overlay => findOverlayByType(overlay, OverlayType.LocationInfoOverlay)
)

export const getAllOffersOverlay = createSelector(getOverlay, overlay =>
  findOverlayByType(overlay, OverlayType.HotelAllOffers)
)

export const getRoomDetailOverlay = createSelector(getOverlay, overlay =>
  findOverlayByType(overlay, OverlayType.RoomDetailsOverlay)
)

export const getRoomOfferPriceBreakdownOverlay = createSelector(
  getOverlay,
  overlay =>
    findOverlayByType(overlay, OverlayType.RoomOfferPriceBreakdownOverlay)
)

export const getDateGuestPickerOverlay = createSelector(
  getOverlay,
  overlays => {
    const matchingOverlay = overlays.find(overlay =>
      [OverlayType.HotelGuestPicker, OverlayType.HotelDatePicker].includes(
        overlay.type
      )
    )

    return matchingOverlay || {type: null, source: null}
  }
)

export const getHotelDescriptionOverlay = createSelector(getOverlay, overlay =>
  findOverlayByType(overlay, OverlayType.HotelDescription)
)

export const getWeb2AppPopupOverlay = createSelector(getOverlay, overlay =>
  findOverlayByType(overlay, OverlayType.Web2AppPopup)
)

export const getStreetViewOverlay = createSelector(getOverlay, overlay =>
  findOverlayByType(overlay, OverlayType.StreetView)
)

export const getPriceWatchOverlay = createSelector(getOverlay, overlay =>
  findOverlayByType(overlay, OverlayType.PriceWatchOverlay)
)

export const getDealDetailsOverlay = createSelector(getOverlay, overlay =>
  findOverlayByType(overlay, OverlayType.DealDetails)
)

export const getBookOnAppConfirmationOverlay = createSelector(
  getOverlay,
  overlay => findOverlayByType(overlay, OverlayType.BookOnAppConfirmation)
)

export const getCashbackInfoOverlay = createSelector(getOverlay, overlay =>
  findOverlayByType(overlay, OverlayType.CashbackInfo)
)

export const getFreezeDetailsOverlayVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.FreezeDetails)

export const getFreezeGuestDetailsOverlayVisibility = (
  state: RootState
): boolean => getOverlayVisibility(state, OverlayType.FreezeGuestDetails)

export const getPriceTrendBottomSheetVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.PriceTrendBottomSheet)

export const getPriceTrendBottomSheetOverlay = createSelector(
  getOverlay,
  overlay => findOverlayByType(overlay, OverlayType.PriceTrendBottomSheet)
)

export const getTrustpilotBottomSheetVisibility = (state: RootState): boolean =>
  getOverlayVisibility(state, OverlayType.TrustpilotBottomSheet)
